import '../css/main.css'
import Bugsnag from '@bugsnag/js'
import BugsnagPluginReact from '@bugsnag/plugin-react'
import DateAdapter from '@mui/lab/AdapterDayjs'
import LocalizationProvider from '@mui/lab/LocalizationProvider'
import { AnimateSharedLayout } from 'framer-motion'
import React, { useEffect, useState } from 'react'
import { CookiesProvider } from 'react-cookie'
import { useCookies } from 'react-cookie'
import ReactGA from 'react-ga4'
import { QueryClientProvider, QueryClient } from 'react-query'

import { AuthProvider } from '../context/AuthContext'
import { QuizBuilderProvider } from '../context/QuizBuilderContext'
import { QuizProvider } from '../context/QuizContext'
import { SignUpProvider } from '../context/SignUpContext'
import { VideoStreamingProvider } from '../context/VideoStreamingContext'

import Maintenance from './maintenance-page'

import { BackgroundMusicProvider } from '~/context/BackgroundMusicContext'
import { SimplifiedProvider } from '~/context/SimplifiedContext'

Bugsnag.start({
  apiKey: process.env.bugsnag_key,
  plugins: [new BugsnagPluginReact()]
})

const ErrorBoundary = Bugsnag.getPlugin('react').createErrorBoundary(React)

function ErrorFallback({ error, clearError }) {
  const isDev = process.env.NODE_ENV === 'development'
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '1rem',
        padding: '2rem',
        fontFamily: 'sans-serif',
        textAlign: 'center'
      }}
    >
      <h1 style={{ fontSize: '1.5rem', fontWeight: 600 }}>
        Something went wrong
      </h1>
      <p style={{ maxWidth: '32rem', color: '#555' }}>
        An unexpected error occurred. Try again, or reload the page.
      </p>
      <div style={{ display: 'flex', gap: '0.75rem' }}>
        <button
          type="button"
          onClick={clearError}
          style={{
            padding: '0.5rem 1rem',
            borderRadius: '0.375rem',
            border: '1px solid #ccc',
            cursor: 'pointer'
          }}
        >
          Try again
        </button>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            padding: '0.5rem 1rem',
            borderRadius: '0.375rem',
            border: 'none',
            background: '#FF3926',
            color: '#fff',
            cursor: 'pointer'
          }}
        >
          Reload page
        </button>
      </div>
      {isDev && error ? (
        <pre
          style={{
            marginTop: '1rem',
            maxWidth: '48rem',
            maxHeight: '40vh',
            overflow: 'auto',
            textAlign: 'left',
            background: '#f6f6f6',
            padding: '1rem',
            borderRadius: '0.375rem',
            fontSize: '0.8rem',
            whiteSpace: 'pre-wrap'
          }}
        >
          {error.stack || error.message || String(error)}
        </pre>
      ) : null}
    </div>
  )
}

function MyApp({ Component, pageProps }) {
  const [cookies] = useCookies(['theme'])
  console.log('initializing app....')
  const [queryClient] = useState(() => new QueryClient())
  const TRACKING_ID = 'G-FRKYELQPS1'
  useEffect(() => {
    ReactGA.initialize(TRACKING_ID)
  }, [])

  useEffect(() => {
    //change the primary color to the theme color
    //get the theme from the cookies
    const theme = cookies.theme
    console.log('theme', theme)
    if (theme !== undefined && theme !== null) {
      console.log('setting primary color', theme)
      // change the  --color-primary to the theme color
      document.documentElement.style.setProperty('--color-primary', '#FF3926')
      document.documentElement.style.setProperty(
        '--color-primary-light',
        '#FFEEEB'
      )
      document.documentElement.style.setProperty('--color-body', '#2f110f')
      document.documentElement.style.setProperty(
        '--color-body-light',
        '#d5cfcf'
      )
    }
  }, [cookies])

  //remove this line to enable maintenance page
  //return <Maintenance />

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <CookiesProvider>
        <SimplifiedProvider>
          <AuthProvider>
            <SignUpProvider>
              <QuizBuilderProvider>
                <QuizProvider>
                  <AnimateSharedLayout>
                    <QueryClientProvider client={queryClient}>
                      <VideoStreamingProvider>
                        <BackgroundMusicProvider>
                          <LocalizationProvider dateAdapter={DateAdapter}>
                            <Component {...pageProps} />
                          </LocalizationProvider>
                        </BackgroundMusicProvider>
                      </VideoStreamingProvider>
                    </QueryClientProvider>
                  </AnimateSharedLayout>
                </QuizProvider>
              </QuizBuilderProvider>
            </SignUpProvider>
          </AuthProvider>
        </SimplifiedProvider>
      </CookiesProvider>
    </ErrorBoundary>
  )
}

export default MyApp
